const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const API_URL = import.meta.env.VITE_API_URL;

interface PopupHandlerOptions {
  onSuccess: (code: string) => Promise<void>;
  onError: (error: Error) => void;
}

export function setupPopupHandler({
  onSuccess,
  onError,
}: PopupHandlerOptions): () => void {
  const handleMessage = async (event: MessageEvent) => {
    // Validate origin
    const authOrigin = new URL(AUTH_URL).origin;
    if (event.origin !== authOrigin) {
      console.warn('Invalid message origin:', event.origin);
      return;
    }

    // Handle OAuth response
    if (event.data.type === 'oauth_response') {
      const { code, state } = event.data;

      // Validate state (CSRF protection)
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        onError(new Error('Invalid state parameter'));
        return;
      }

      try {
        await onSuccess(code);
      } catch (error) {
        onError(error as Error);
      }
    } else if (event.data.type === 'oauth_error') {
      onError(
        new Error(event.data.error_description || 'Authentication failed'),
      );
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
}

export async function exchangeCodeForToken(code: string): Promise<{
  customToken: string;
  organization_id?: string;
}> {
  const codeVerifier = sessionStorage.getItem('code_verifier');
  const state = sessionStorage.getItem('oauth_state');

  if (!codeVerifier || !state) {
    throw new Error('Missing PKCE parameters');
  }

  const response = await fetch(`${API_URL}/api/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, codeVerifier, state, redirectUri: `${window.location.origin}/auth/callback` }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Token exchange failed');
  }

  const data = await response.json();
  return {
    customToken: data.customToken,
    organization_id: data.organization_id,
  };
}
